import { tryParseJSONObject } from '@fox/atoms';
import { TICKET_BLOCK_TYPE } from '../../../../form-block-types';
import { ReservationModel, ReservationStatus } from '../../../form-event-context/contracts';
import { IQuestionAnswer } from '../../../form-root/contracts';
import { TICKET_RESERVATION_MESSAGE_KEY, TicketAnswer, TicketAnswerData, TicketFieldProps } from '../../ticket-block/contracts';
import { buildTicketLineItem, getPeopleCountFromAnswers, reserveTickets } from '../../ticket-block/helpers';
import { FormPageValidatorFunctionType } from './form-page-validators';

export const validateTicketBlock: FormPageValidatorFunctionType = async ({
	field,
	answers,
	isFinalPage,
	ownerId: eventId,
	formEventApiHelpers,
	onWaitList,
	sections,
	formRequestId
}) => {
	const answer: IQuestionAnswer<TicketAnswerData> | undefined = answers.find(answer => answer.questionName === field.name);
	const parsedAnswer = tryParseJSONObject(answer?.answer?.answerJson, {}) as TicketAnswer;
	const metadata = tryParseJSONObject(field.metadataJson, {}) as TicketFieldProps;
	const token = (window as any).validatedFormToken;
	const reservationId = answer?.data?.reservation?.id;
	const selectedAnswerIndex = answers.findIndex(a => a.questionName === field.name);

	// Incomplete answer
	if ((!metadata.required && !answer?.data?.lineItems) || onWaitList) {
		if (selectedAnswerIndex >= 0) {
			// Delete answer if it exists as it is not valid/incomplete/irrelevant
			answers.splice(selectedAnswerIndex, 1);
		}
		return {
			success: true
		};
	}
	const latestReservation = await formEventApiHelpers.findReservationById({ reservationId, token });
	const ticketReservation = { current: latestReservation?.reservation };
	const ticketStatus = ticketReservation?.current?.status;
	let finalAnswer: TicketAnswer = parsedAnswer;
	if (!latestReservation) {
		return { success: false, errorMessage: 'No tickets reserved' };
	}

	const reservedCount = ticketReservation?.current?.count ?? 0;
	const peopleCount = getPeopleCountFromAnswers(answers, sections);
	if (reservedCount < peopleCount) {
		return { success: false, errorMessage: 'Not enough tickets for contacts entered.' };
	}
	if (ticketStatus === ReservationStatus.Confirmed) {
		return { success: true };
	}
	// Already have a pending reservation during next page no action needed
	if (!isFinalPage && ticketStatus === ReservationStatus.Pending) {
		return { success: true };
	}

	const endTime = ticketReservation?.current?.expiry;
	const currentTime = new Date().getTime();
	const end = endTime ? new Date(endTime).getTime() : undefined;

	let updatedReservation: ReservationModel | undefined;
	let isCapped: boolean | undefined;
	let couldNotExtend = false;

	if (ticketReservation?.current?.status === ReservationStatus.Confirmed) {
		return { success: true };
	} else if (isFinalPage && ticketReservation?.current?.status === ReservationStatus.Pending) {
		// Extend reservation
		const extendedRes = await formEventApiHelpers.extendReservation({
			formRequestId,
			reservationId: ticketReservation.current.id,
			messageKey: TICKET_RESERVATION_MESSAGE_KEY,
			safeExpiry: true,
			token
		});
		updatedReservation = extendedRes.reservation;
		couldNotExtend = !updatedReservation;
	} else if (isFinalPage && ((end && currentTime >= end) || ticketStatus === ReservationStatus.Expired || couldNotExtend)) {
		// If expired try create new reservation on final submit
		const reservationResponse = await reserveTickets({
			token,
			newAnswer: parsedAnswer,
			reservation: ticketReservation,
			reserveTicketMutation: formEventApiHelpers.createTicketReservation,
			eventId,
			formRequestId,
			enforceCount: true
		});
		updatedReservation = reservationResponse?.reservation;
		isCapped = reservationResponse?.isCapped;

		if (isCapped) {
			finalAnswer = { ...finalAnswer, quantity: updatedReservation?.count };
		}
	} else if (ticketReservation?.current?.status === ReservationStatus.Expired) {
		return { success: false, errorMessage: 'Ticket reservation expired' };
	}

	const availableTickets = await formEventApiHelpers.getTicketDetails({ eventId, allowedTickets: metadata.allowedTickets, token });
	const answerToSave = {
		questionName: field.name,
		answer: {
			answerJson: JSON.stringify(finalAnswer)
		},
		data: {
			lineItems: buildTicketLineItem(availableTickets?.tickets ?? [], finalAnswer),
			reservation: updatedReservation
		},
		type: TICKET_BLOCK_TYPE
	};

	if (selectedAnswerIndex >= 0) {
		answers[selectedAnswerIndex] = answerToSave;
	} else {
		answers.push(answerToSave);
	}
	//will need to come from a context
	//await formQueryClient.refetchQueries(['available_tickets', metadata.allowedTickets]);
	if (updatedReservation?.status === ReservationStatus.Pending) {
		return { success: true };
	} else {
		return { success: false, errorMessage: 'Unable to reserve tickets' };
	}
};
